// components/RecordingStudio/VoiceSelector.jsx
import React, { useState, useEffect, useRef } from "react";
import { X, Play, Pause, Search, Star, ChevronLeft, Globe } from "lucide-react";
import api from "../../services/api";

// ─── Tier badge config ────────────────────────────────────────────────────────

const TIER_BADGE = {
  chirp3: {
    label: "Chirp 3 HD",
    className: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  },
  chirp: {
    label: "Chirp HD",
    className: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  },
  studio: {
    label: "Studio",
    className: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  },
  neural2: {
    label: "Neural2",
    className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  },
  news: {
    label: "News",
    className: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  },
  wavenet: {
    label: "WaveNet",
    className: "bg-teal-500/20 text-teal-300 border-teal-500/30",
  },
  standard: {
    label: "Standard",
    className: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  },
};

// ─── Human-readable locale labels ─────────────────────────────────────────────

const LOCALE_LABELS = {
  "en-GB": "English (UK)",
  "en-US": "English (US)",
  "en-AU": "English (AU)",
  "en-IN": "English (IN)",
  "en-CA": "English (CA)",
  "es-ES": "Spanish (Spain)",
  "es-US": "Spanish (US)",
  "fr-FR": "French",
  "fr-CA": "French (CA)",
  "de-DE": "German",
  "it-IT": "Italian",
  "pt-BR": "Portuguese (BR)",
  "pt-PT": "Portuguese (PT)",
  "nl-NL": "Dutch",
  "pl-PL": "Polish",
  "ru-RU": "Russian",
  "ja-JP": "Japanese",
  "ko-KR": "Korean",
  "zh-CN": "Chinese (Simplified)",
  "zh-TW": "Chinese (Traditional)",
  "ar-XA": "Arabic",
  "hi-IN": "Hindi",
  "tr-TR": "Turkish",
  "sv-SE": "Swedish",
  "da-DK": "Danish",
  "fi-FI": "Finnish",
  "nb-NO": "Norwegian",
  "cs-CZ": "Czech",
  "sk-SK": "Slovak",
  "ro-RO": "Romanian",
  "hu-HU": "Hungarian",
  "uk-UA": "Ukrainian",
  "el-GR": "Greek",
  "he-IL": "Hebrew",
  "id-ID": "Indonesian",
  "ms-MY": "Malay",
  "th-TH": "Thai",
  "vi-VN": "Vietnamese",
  "bg-BG": "Bulgarian",
  "ca-ES": "Catalan",
  "hr-HR": "Croatian",
  "lt-LT": "Lithuanian",
  "lv-LV": "Latvian",
  "et-EE": "Estonian",
  "sl-SI": "Slovenian",
  "sr-RS": "Serbian",
  "af-ZA": "Afrikaans",
  "sw-KE": "Swahili",
  "bn-IN": "Bengali",
  "gu-IN": "Gujarati",
  "kn-IN": "Kannada",
  "ml-IN": "Malayalam",
  "mr-IN": "Marathi",
  "pa-IN": "Punjabi",
  "ta-IN": "Tamil",
  "te-IN": "Telugu",
  "ur-IN": "Urdu",
  "fil-PH": "Filipino",
  "cmn-CN": "Mandarin (CN)",
  "cmn-TW": "Mandarin (TW)",
  "yue-HK": "Cantonese (HK)",
};

const localeLabel = (code) => LOCALE_LABELS[code] || code;

// ─── LocalePicker overlay ──────────────────────────────────────────────────────

const LocalePicker = ({ voice, onConfirm, onBack }) => {
  const [selected, setSelected] = useState(voice.languageCodes[0]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-6 border-b border-[#22262F]">
        <button
          onClick={onBack}
          className="p-2 hover:bg-[#22262F] rounded-lg transition-colors text-gray-400 hover:text-white"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden flex-shrink-0">
            {voice.avatarImageUrl ? (
              <img
                src={voice.avatarImageUrl}
                alt={voice.displayName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <span
              className="text-sm font-bold text-white"
              style={{ display: voice.avatarImageUrl ? "none" : "flex" }}
            >
              {voice.displayName[0]}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-none">
              {voice.displayName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 capitalize">
              {voice.gender} · {voice.accent || "Multilingual"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-gray-500 text-xs">
          <Globe size={12} />
          <span>{voice.languageCodes.length} locales</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-sm text-gray-400 mb-4">
          This voice supports multiple languages. Choose which locale to use for
          this block.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {voice.languageCodes.map((code) => {
            const isSelected = selected === code;
            return (
              <button
                key={code}
                onClick={() => setSelected(code)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                  isSelected
                    ? "border-purple-500/60 bg-purple-500/10"
                    : "border-[#22262F] hover:border-purple-500/30 bg-[#0C0E12]/40 hover:bg-[#0C0E12]/80"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors ${
                    isSelected ? "bg-purple-400" : "bg-gray-700"
                  }`}
                />
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium leading-none ${isSelected ? "text-white" : "text-gray-300"}`}
                  >
                    {localeLabel(code)}
                  </p>
                  <p className="text-[10px] text-gray-600 font-mono mt-0.5">
                    {code}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-[#22262F]">
        <button
          onClick={() => onConfirm(selected)}
          className="w-full py-3 px-6 rounded-xl text-sm font-bold text-white transition-all"
          style={{
            background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
            boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
          }}
        >
          Use {voice.displayName} · {localeLabel(selected)}
        </button>
      </div>
    </div>
  );
};

// ─── VoiceCard ────────────────────────────────────────────────────────────────

const VoiceCard = ({ voice, onSelect, playingId, onTogglePlay }) => {
  const isPlaying = playingId === voice.id;
  const tier = TIER_BADGE[voice.tier] || TIER_BADGE.standard;
  const isMultiLocale = voice.languageCodes?.length > 1;

  return (
    <div
      className="flex flex-col items-center p-4 rounded-xl border border-[#22262F] hover:border-purple-500/60 transition-all cursor-pointer group bg-[#0C0E12]/40 hover:bg-[#0C0E12]/80"
      onClick={() => onSelect(voice)}
    >
      {/* Avatar */}
      <div className="relative mb-3">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-purple-500/40 transition-all">
          {voice.avatarImageUrl ? (
            <img
              src={voice.avatarImageUrl}
              alt={voice.displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <span
            className="text-2xl font-bold text-white"
            style={{ display: voice.avatarImageUrl ? "none" : "flex" }}
          >
            {voice.displayName[0]}
          </span>
        </div>

        {/* Premium star */}
        {voice.isPremium && (
          <div className="absolute -top-1 -left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
            <Star size={10} fill="white" className="text-white" />
          </div>
        )}

        {/* Multi-locale badge */}
        {isMultiLocale && (
          <div className="absolute -bottom-1 -left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#13161B] border border-[#22262F]">
            <Globe size={9} className="text-purple-400" />
            <span className="text-[9px] text-purple-400 font-mono">
              {voice.languageCodes.length}
            </span>
          </div>
        )}

        {/* Play sample button */}
        {voice.sampleAudioUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePlay(voice);
            }}
            className="absolute -bottom-1 -right-1 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-purple-700 shadow-lg"
          >
            {isPlaying ? (
              <Pause size={12} fill="white" className="text-white" />
            ) : (
              <Play size={12} fill="white" className="text-white" />
            )}
          </button>
        )}
      </div>

      {/* Name */}
      <p className="font-semibold text-white text-sm text-center leading-tight mb-1">
        {voice.displayName}
      </p>

      {/* Gender + accent */}
      <p className="text-xs text-gray-500 capitalize mb-2">
        {voice.gender} · {voice.accent || "British"}
      </p>

      {/* Tier badge */}
      <span
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full border mb-2 ${tier.className}`}
      >
        {tier.label}
      </span>

      {/* Locale pills — show up to 3 */}
      {voice.languageCodes?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center mb-1">
          {voice.languageCodes.slice(0, 3).map((code) => (
            <span
              key={code}
              className="text-[9px] px-1.5 py-0.5 bg-[#22262F] text-gray-500 rounded font-mono"
            >
              {code}
            </span>
          ))}
          {voice.languageCodes.length > 3 && (
            <span className="text-[9px] px-1.5 py-0.5 bg-[#22262F] text-gray-600 rounded">
              +{voice.languageCodes.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Characteristics */}
      {voice.characteristics?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center">
          {voice.characteristics.slice(0, 2).map((c) => (
            <span
              key={c}
              className="text-[10px] px-1.5 py-0.5 bg-[#22262F] text-gray-400 rounded"
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── VoiceSelector ────────────────────────────────────────────────────────────

const LANGUAGES = [
  { label: "All languages", code: "" },
  // English
  { label: "English (UK)", code: "en-GB" },
  { label: "English (US)", code: "en-US" },
  { label: "English (AU)", code: "en-AU" },
  { label: "English (IN)", code: "en-IN" },
  // Western European
  { label: "Spanish (Spain)", code: "es-ES" },
  { label: "Spanish (US)", code: "es-US" },
  { label: "French", code: "fr-FR" },
  { label: "French (CA)", code: "fr-CA" },
  { label: "German", code: "de-DE" },
  { label: "Italian", code: "it-IT" },
  { label: "Portuguese (BR)", code: "pt-BR" },
  { label: "Dutch", code: "nl-NL" },
  // Northern / Eastern European
  { label: "Swedish", code: "sv-SE" },
  { label: "Danish", code: "da-DK" },
  { label: "Finnish", code: "fi-FI" },
  { label: "Norwegian", code: "nb-NO" },
  { label: "Polish", code: "pl-PL" },
  { label: "Russian", code: "ru-RU" },
  { label: "Czech", code: "cs-CZ" },
  { label: "Romanian", code: "ro-RO" },
  { label: "Hungarian", code: "hu-HU" },
  { label: "Greek", code: "el-GR" },
  { label: "Hebrew", code: "he-IL" },
  // South / Southeast Asian
  { label: "Hindi", code: "hi-IN" },
  { label: "Indonesian", code: "id-ID" },
  { label: "Thai", code: "th-TH" },
  { label: "Vietnamese", code: "vi-VN" },
  // East Asian
  { label: "Japanese", code: "ja-JP" },
  { label: "Korean", code: "ko-KR" },
  // Middle East / Africa
  { label: "Arabic", code: "ar-XA" },
  { label: "Turkish", code: "tr-TR" },
  { label: "Swahili", code: "sw-KE" },
];

const VoiceSelector = ({ onSelect, onClose }) => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Filters
  const [langCode, setLangCode] = useState("en-GB");
  const [gender, setGender] = useState("");
  const [tier, setTier] = useState("");
  const [isPremium, setIsPremium] = useState("");
  const [search, setSearch] = useState("");

  // Multi-locale picker state
  const [pendingVoice, setPendingVoice] = useState(null);

  // Audio preview
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // ── Load voices ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const controller = new AbortController();
    loadVoices(controller.signal);
    return () => controller.abort();
  }, [langCode, gender, tier, isPremium, search]);

  const loadVoices = async (signal) => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      // Only pass languageCode to the API if a specific one is selected
      if (langCode) params.languageCode = langCode;
      if (gender) params.gender = gender;
      if (tier) params.tier = tier;
      if (isPremium) params.isPremium = isPremium;
      if (search) params.search = search;

      const response = await api.getVoices(params.languageCode || "", params);
      setVoices(response.avatars || []);
      setTotal(response.total || 0);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Failed to load voices:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Audio preview ───────────────────────────────────────────────────────────

  const handleTogglePlay = (voice) => {
    if (!voice.sampleAudioUrl) return;

    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(voice.sampleAudioUrl);
    audioRef.current = audio;
    audio.play().catch(console.error);
    audio.onended = () => setPlayingId(null);
    setPlayingId(voice.id);
  };

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  // ── Voice card click ────────────────────────────────────────────────────────

  const handleCardClick = (voice) => {
    if (voice.languageCodes?.length > 1) {
      // Stop any playing preview before showing picker
      audioRef.current?.pause();
      setPlayingId(null);
      setPendingVoice(voice);
    } else {
      // Single-locale voice — select immediately using that locale (or en-GB fallback)
      const locale = voice.languageCodes?.[0] ?? "en-GB";
      onSelect({ ...voice, selectedLanguageCode: locale });
    }
  };

  const handleLocaleConfirm = (locale) => {
    onSelect({ ...pendingVoice, selectedLanguageCode: locale });
    setPendingVoice(null);
  };

  const resetFilters = () => {
    setGender("");
    setTier("");
    setIsPremium("");
    setSearch("");
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#13161B] rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* ── Locale picker overlay ── */}
        {pendingVoice && (
          <LocalePicker
            voice={pendingVoice}
            onConfirm={handleLocaleConfirm}
            onBack={() => setPendingVoice(null)}
          />
        )}

        {/* ── Main selector (hidden behind picker) ── */}
        <div
          className={`flex flex-col flex-1 min-h-0 transition-opacity ${pendingVoice ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#22262F] flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-white">Choose a Voice</h2>
              <p className="text-sm text-gray-400 mt-0.5">
                {loading ? "Loading…" : `${total} voices available`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#22262F] rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 border-b border-[#22262F] flex flex-wrap items-center gap-3 flex-shrink-0">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                size={15}
              />
              <input
                type="text"
                placeholder="Search voices…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white placeholder-gray-600 text-sm focus:outline-none focus:border-purple-500/60 transition-colors"
              />
            </div>

            {/* Language filter */}
            <select
              value={langCode}
              onChange={(e) => setLangCode(e.target.value)}
              className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>

            {/* Gender */}
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
            >
              <option value="">All genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>

            {/* Tier */}
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
            >
              <option value="">All tiers</option>
              <option value="chirp3">Chirp 3 HD</option>
              <option value="chirp">Chirp HD</option>
              <option value="studio">Studio</option>
              <option value="neural2">Neural2</option>
              <option value="wavenet">WaveNet</option>
              <option value="standard">Standard</option>
            </select>

            {/* Premium toggle */}
            <select
              value={isPremium}
              onChange={(e) => setIsPremium(e.target.value)}
              className="px-3 py-2 bg-[#0C0E12] border border-[#22262F] rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/60"
            >
              <option value="">All voices</option>
              <option value="true">Premium only</option>
              <option value="false">Standard only</option>
            </select>

            <button
              onClick={resetFilters}
              className="px-3 py-2 text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mr-3" />
                Loading voices…
              </div>
            ) : voices.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <p className="font-medium">No voices match your filters</p>
                <button
                  onClick={resetFilters}
                  className="mt-2 text-purple-400 hover:text-purple-300 text-sm"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {voices.map((voice) => (
                  <VoiceCard
                    key={voice.id}
                    voice={voice}
                    onSelect={handleCardClick}
                    playingId={playingId}
                    onTogglePlay={handleTogglePlay}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
